// ./src/components/features/Tasks/taskHelpers.ts
//
// Shared presentation helpers for tasks / appointments: type + status labels and
// colors, duration formatting, and the "how long has this been worked on" derivation
// (accumulated tracked time plus any currently-running segment).

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { Task, TASK_STATUS, TASK_TYPE, TASK_RESPONSIBLE_KIND, TASK_LOCATION_KIND } from "@/utils/api";

dayjs.extend(utc);
dayjs.extend(timezone);

const resolveTz = (tz?: string | null): string => {
  if (tz) return tz;
  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

/** A stored-UTC schedule, rendered in the event's timezone (e.g. "Jul 14, 3:00 PM"). */
export const formatScheduledAt = (utcStr?: string | null, tz?: string | null): string | null => {
  if (!utcStr) return null;
  const d = dayjs.utc(utcStr);
  if (!d.isValid()) return null;
  return d.tz(resolveTz(tz)).format("MMM D, h:mm A");
};

export const TASK_TYPE_LABELS: Record<string, string> = {
  [TASK_TYPE.TASK]: "Task",
  [TASK_TYPE.APPOINTMENT]: "Appointment",
};

export const getTaskTypeLabel = (type?: string | null): string =>
  (type && TASK_TYPE_LABELS[type]) || "Task";

export const TASK_STATUS_LABELS: Record<string, string> = {
  [TASK_STATUS.TODO]: "To do",
  [TASK_STATUS.IN_PROGRESS]: "In progress",
  [TASK_STATUS.PAUSED]: "Paused",
  [TASK_STATUS.DONE]: "Done",
};

export const getTaskStatusLabel = (status?: string | null): string =>
  (status && TASK_STATUS_LABELS[status]) || "To do";

// Mantine color keys per lifecycle state.
export const TASK_STATUS_COLORS: Record<string, string> = {
  [TASK_STATUS.TODO]: "gray",
  [TASK_STATUS.IN_PROGRESS]: "blue",
  [TASK_STATUS.PAUSED]: "yellow",
  [TASK_STATUS.DONE]: "green",
};

export const getTaskStatusColor = (status?: string | null): string =>
  (status && TASK_STATUS_COLORS[status]) || "gray";

export const TASK_RESPONSIBLE_KIND_LABELS: Record<string, string> = {
  [TASK_RESPONSIBLE_KIND.USER]: "Me",
  [TASK_RESPONSIBLE_KIND.CREW]: "Crew",
  [TASK_RESPONSIBLE_KIND.GUEST]: "Guest",
  [TASK_RESPONSIBLE_KIND.VENDOR]: "Vendor",
  [TASK_RESPONSIBLE_KIND.VENUE]: "Venue",
};

export const getResponsibleKindLabel = (kind?: string | null): string =>
  (kind && TASK_RESPONSIBLE_KIND_LABELS[kind]) || "";

export const getResponsibleKindIcon = (kind?: string | null): string => {
  switch (kind) {
    case TASK_RESPONSIBLE_KIND.USER:
      return "UserCheck";
    case TASK_RESPONSIBLE_KIND.CREW:
      return "Users";
    case TASK_RESPONSIBLE_KIND.GUEST:
      return "User";
    case TASK_RESPONSIBLE_KIND.VENDOR:
      return "Briefcase";
    case TASK_RESPONSIBLE_KIND.VENUE:
      return "MapPin";
    default:
      return "User";
  }
};

export const getLocationKindIcon = (kind?: string | null): string => {
  switch (kind) {
    case TASK_LOCATION_KIND.VENUE:
      return "MapPin";
    case TASK_LOCATION_KIND.ADDRESS:
      return "MapPin";
    case TASK_LOCATION_KIND.ONLINE:
      return "Video";
    default:
      return "MapPin";
  }
};

/** Display text for a task's location (prefers the denormalized snapshot). */
export const formatTaskLocation = (task: Task): string | null => {
  if (task.locationLabel) return task.locationLabel;
  if (task.locationKind === TASK_LOCATION_KIND.ADDRESS) return task.locationAddress || null;
  if (task.locationKind === TASK_LOCATION_KIND.ONLINE) return "Online";
  return null;
};

/** A Google Maps link for an ADDRESS location (precise pin from coords, else a text search). */
export const buildTaskMapsUrl = (task: Task): string | null => {
  if (task.locationKind !== TASK_LOCATION_KIND.ADDRESS) return null;
  if (task.latitude != null && task.longitude != null) {
    return `https://www.google.com/maps/search/?api=1&query=${task.latitude},${task.longitude}`;
  }
  const q = (task.locationAddress || task.locationLabel || "").trim();
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

/** An "open in maps" link for an ONLINE location = the meeting URL itself. */
export const taskOnlineUrl = (task: Task): string | null =>
  task.locationKind === TASK_LOCATION_KIND.ONLINE ? task.onlineMeetingUrl || null : null;

/** "1h 30m" / "45m" / "30s" / "0m" from a whole-seconds duration. */
export const formatDurationSec = (totalSec?: number | null): string => {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  if (s < 60) return `${s}s`;

  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
};

/** "1h 30m" from a minutes value (expected duration). */
export const formatDurationMin = (totalMin?: number | null): string => {
  const m = Math.max(0, Math.floor(Number(totalMin) || 0));
  const hours = Math.floor(m / 60);
  const minutes = m % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
};

/**
 * Tracked seconds so far: the server-accumulated total plus, when the task is
 * currently running, the open segment from `activeSince` to `nowMs`.
 */
export const deriveElapsedSec = (task: Task, nowMs: number): number => {
  const accrued = Math.max(0, Math.floor(Number(task.actualDurationSec) || 0));

  if (task.taskStatus === TASK_STATUS.IN_PROGRESS && task.activeSince) {
    const started = new Date(task.activeSince).getTime();
    if (!Number.isNaN(started)) {
      return accrued + Math.max(0, Math.floor((nowMs - started) / 1000));
    }
  }

  return accrued;
};

export const isTaskRunning = (task: Task): boolean => task.taskStatus === TASK_STATUS.IN_PROGRESS;
export const isTaskDone = (task: Task): boolean => task.taskStatus === TASK_STATUS.DONE;
